<template>
  
  <ion-page id="legacy-rep-tree-page">
    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>
 
    <ion-content>

    <div v-show="showTour">
       <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
    </div>

    <div id="the_comment" :style="styleComment">
      <p>{{ commentString }}</p>
    </div>

    <div id="the_board" :style="styleBoard">
      <CMChessboard :fen="fen" :orientation="orientation"
              :boardSize="boardSize"
              :arrows="arrowPairs" @board-move="boardMove" />
    </div>

    <div id="the_tree" :style="styleRepTree">
      <RepTreeNewView 
                :treeDisplay="treeDisplay"
                :treeTrigger="treeTrigger" 
                :showComments="showComments"
                @move-click="repTreeMoveClick"
                @branch-click="repTreeBranchClick"/>
      <!-- <LegacyRepTree :moveDirection="directionTrigger" 
              calledFrom="tree" 
              :reloadRepertoire="reloadTrigger"
              :showComments="showComments"
              @move-click="moveClick" @branch-click="branchClick" @repertoire-comment="updateComment"
              @repertoire-info="repertoireInfo" @toolbar-update="toolbarUpdate" /> -->
    </div>

    </ion-content>
    <Toolbar id="the_toolbar" :ignoreClicks="showTour" :elements="toolbarElements" 
                    :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />

  </ion-page>
  
</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import AlertReplacement from '@/components/AlertReplacement.vue'
  import RepTreeNewView from '@/components/RepTreeNewView.vue';
  import { t } from '@/composables/translateText.js'

  import Toolbar from '@/components/Toolbar.vue'
  import CMChessboard from '@/components/CMChessboard.vue'
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import { ref, watch } from 'vue'
  import { onMounted, onBeforeMount } from 'vue'

  import { useConnectionStore } from '@/stores/connectionStore';
  import { useGetData } from '@/composables/getData.js'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import Tour from '@/components/Tour.vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import { buildPieceLocationStrings, getPieceSetName, columnToNumber, squarePairToAlgebraic } from '@/composables/blindfoldSupport.js'

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  const reloadTrigger = ref(0)
  
  const initialFenid4 = "147869767"
  const initialFenidCombined = "0~" + initialFenid4
  var initialBoardSelectedInTree = true

  var repTree = new Map()
  var displayTreeMap = new Map()
  var treeDisplay = ref([])

  var saveTrimMoveHistoryStack = []
  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  var moveHistoryStackAtGameStart = []

  var backupHasBeenMade = false
  var trimFromCombined = ""
  var trimToCombined = ""

  const treeTrigger = ref(0)

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  const connectionStore = useConnectionStore()

  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  const showTour = ref((tourOption == "show"))
  
  const COMMENT_FONTSIZE = 16
  const COMMENT_HEIGHT = COMMENT_FONTSIZE * 1.2 * 2 + 4
  
  const commentString = ref("Retrieving repertoire")

  var directionTriggerCounter = 1
  const directionTrigger = ref(1)

  //console.log("############################### in reptree")
  const TOOLBAR_BACK = 0
  const TOOLBAR_FORWARD = 1
  const TOOLBAR_COMMENTS = 2
  const TOOLBAR_TRIM = 3

  const toolbarElements = ref([
    { index : TOOLBAR_BACK, label : "<" ,disabled : true },
    { index : TOOLBAR_FORWARD, label : ">" ,disabled : false },
    { index : TOOLBAR_COMMENTS, label : t("Hide Comments") ,disabled : false },
    { index : TOOLBAR_TRIM, label : t("Trim") ,disabled : true },
  ])

  var playingWhite = 1

  var isLastMoveOnBranch = false

  var repertoireId = "0"

  var repertoireRatingMin = 0
  var repertoireRatingMax = 0
  var repertoireName = ""
  var repertoirePercentage = 0
  const repertoireDescription = ref("Loading Repertoire Info...")

  const SHOW_TREE = 0
  const SHOW_HELP = 1
  const SHOW_REPERTOIRE_LIST = 2
  const SHOW_SUBSCRIBERS_ONLY = 3

  const showState = ref(SHOW_TREE)
  const showComments = ref(true)

  const toolbarTrigger = ref(0)

  var blankAppendTrigger = " "
  const orientation = ref("white")
  const fen = ref(INITIAL_FEN)
  const arrowPairs = ref([])

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  
  //console.log("**** explorer initial screensize", windowSizeRef.value)

  const boardSize = ref(300)

  // tour stuff
  const tourInfo = ref({
    id : "repertoire_tree",
    video : "https://www.chessvis.com/videos/repertoires_on_web.mp4",
    elements : [
      { id: "", title: "Repertoire Tree Viewer", text : "This is a '<i>Bird's Eye View</i>' of your repertoire'.<br><br>As you navigate around the tree, the board will be updated to reflect the moves to that point.<br><br>The video is for the entire set of repertoire routines."},
      { id: "the_tree", title: "The Tree", text : "Moves that have a <b>(+)</b> after them represent places where there are multiple move choices. Tapping the <b>(+)</b> will open up the branches and highlight the choices on the board and in the tree."},
      { id: "the_comment", title: "Move Comments", text : "If there is a comment associated with the move, it will be displayed here."},
      { id: "the_toolbar", title: "Hide Comments - Toolbar", text : "By default, comments are shown within the tree text.<br><br>If you find that distracting, or want a more compact 'tree' then this will toggle the comments out of and back into view."},
      { id: "the_toolbar", title: "Repertoire - Toolbar", text : "This will bring up the list of repertoires so you can select a different one."},
    ]
  })

  const styleRepTree = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleComment = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : COMMENT_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })


  function afterStartupCallbackRoutine(stuffChanged) {

    // this can be used to switch away from non-subscribers
    if (connectionStore.getIsSubscriber != 1) {
      showState.value = SHOW_SUBSCRIBERS_ONLY
      return
    }
    updateWidthHeights()
    menuTrigger.value++
    getRepertoireInfo()
  }

  doStartupCheck(false,afterStartupCallbackRoutine)

  function updateComment(comment) {
    commentString.value = comment
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function repertoireInfo(playingColorName,repertoireName) {
    //console.log("repertoire info coming back",playingColorName,repertoireName)
    commentString.value = repertoireName

    incrementAppendTrigger()
    orientation.value = playingColorName + blankAppendTrigger
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boardSize.value + "px"
    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"

    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleRepTree.value.height = boundaries['tree'].height + "px"
    styleRepTree.value.width = boundaries['tree'].width + "px"
    styleRepTree.value.top = boundaries['tree'].top + "px"
    styleRepTree.value.left = boundaries['tree'].left + "px"

    styleComment.value.height = boundaries['comment'].height + "px"
    styleComment.value.width = boundaries['comment'].width + "px"
    styleComment.value.top = boundaries['comment'].top + "px"
    styleComment.value.left = boundaries['comment'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
   
  }

  function calculatePortraitWidthHeights(availableSpace) {

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height / 2,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    
    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const commentPositionTop = boardPositionTop + boardHeight
    const commentPositionLeft = availableSpace.leftRightIndent
    const commentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const treePositionTop = commentPositionTop + COMMENT_HEIGHT
    const treePositionLeft = availableSpace.leftRightIndent
    const treeHeight = availableSpace.height - treePositionTop
    const treeWidth = commentWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop + availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop + availableSpace.topIndent
      },
      comment : {
        width : commentWidth,
        height : COMMENT_HEIGHT,
        left : commentPositionLeft + availableSpace.leftIndent,
        top : commentPositionTop + availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    // reserve space for the tree
    const availableSpaceForBoard = { width : availableSpace.width / 2,
                height : availableSpace.height - COMMENT_HEIGHT,
                isPortrait : false }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    const commentPositionTop = 0
    const commentPositionLeft = availableSpace.leftRightIndent
    const commentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y + COMMENT_HEIGHT
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const treePositionTop = COMMENT_HEIGHT
    const treePositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const treeHeight = availableSpace.height - COMMENT_HEIGHT
    const treeWidth = availableSpace.width - treePositionLeft

    
    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop + availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop + availableSpace.topIndent
      },
      comment : {
        width : commentWidth,
        height : COMMENT_HEIGHT,
        left : commentPositionLeft + availableSpace.leftIndent,
        top : commentPositionTop + availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** REP TREE -- window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** REP TREE new window size",windowSizeRef.value)
    updateWidthHeights()
    menuTrigger.value++
  })
  
  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function boardMove(arrayIndex,from,to) {
  }

  // this version recursively opens as many branches as the user has set to open
  function repTreeBranchClick(branchElement) {
    repTreeMoveClick(branchElement)

    arrowPairs.value = []
    //console.log("**** in branch click",branchElement)
    const move = branchElement.move
    fen.value = move.fen

    move.branchIsOpen = !move.branchIsOpen
    buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0)

    findMoveInTreeDisplay()
    if (move.branchIsOpen == false)
      return

    // build arrows
    const nextMoves = repTree.get(move.to)
    for (var i=0; i < nextMoves.length; i++) {
      var oneMove = nextMoves[i]
      const pairNumber = parseInt(oneMove.toSquare) + parseInt(oneMove.fromSquare) * 100
      //console.log("Branch to ",oneMove,pairNumber)
      const arrowString = squarePairToAlgebraic(pairNumber)
      arrowPairs.value.push(arrowString)
    }
    //console.log("ARROW PAIRS",arrowPairs)
  }

  function clearTreeDisplaySelectedFlag() {
    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++)
        treeDisplay.value[i].moves[j].selected = false
    }
  }

  function repTreeMoveClick(branchElement) {
    arrowPairs.value = []

    //console.log("**** in move click",branchElement)
     
    clearTreeDisplaySelectedFlag()
  
    branchElement.selected = true
    treeTrigger.value++

    const move = branchElement.move
    fen.value = move.fen
    commentString.value = move.comment

    const fenidCombines = move.fenidCombinedStackString.split(",")

    //console.log("fenidcombines",fenidCombines)

    moveHistoryStack.value = []

    if (fenidCombines.length <= 1 || (fenidCombines.length == 2 && fenidCombines[0] == "0~0")) {
      initialBoardSelectedInTree = true
      fen.value = INITIAL_FEN
      toolbarUpdate(true,false)
      //console.log("INITIAL FEN")
      return
    }

    toolbarUpdate(false,false)

    initialBoardSelectedInTree = false
    for (var i=0; i < fenidCombines.length - 1; i++) {
      const move = repTree.get(fenidCombines[i])
      //console.log("move in loop",move)
      if (move == null)
        break

      var oneMove = null
      for (var j=0; j < move.length; j++) {
        //console.log("looking for i,j",i,j,move[j].to,fenidCombines[i+1])
        if (move[j].to == fenidCombines[i+1]) {
          fen.value = move[j].fen
          const san = move[j].san
          const boardToSquare = move[j].toSquare
          const boardFromSquare = move[j].fromSquare
          const percent = move[j].move_percentage
          oneMove = {fen : fen.value, 
                          fromCombined : move[j].from,
                          toCombined : move[j].to, 
                          boardToSquare : boardToSquare,
                          boardFromSquare : boardFromSquare,
                          san : san, movePercentage : percent,
                          comment : move[j].comment }
          moveHistoryStack.value.push(oneMove)
          break;
        }
      }
    }

    // is there a move after this?
    var canForwardFromHere = true
    const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
    //console.log("Last Move on the stack",oneMoveHistory)
    const lastMoveToCombined = oneMoveHistory.toCombined
    if (repTree.has(lastMoveToCombined) == false)
      canForwardFromHere = false
    else {
      canForwardFromHere = true
      // const nextMoves = repTree.get(lastMoveToCombined)
      // console.log("NEXT MOVES ",nextMoves)
      // const nextMove = nextMoves[0]
      // canForwardFromHere = repTree.has(nextMove.to)
    }
    toolbarUpdate(false,!canForwardFromHere)

    //console.log("movehistorystack",moveHistoryStack.value)
  }

  function findMoveInTreeRecursive(parentFenidCombined,moveCombinedString) {
    const moves = repTree.get(parentFenidCombined)
    var found = false

    if (moves == null)
      return (false)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
        const toCombined = moves[i].to

        if (moves[i].fenidCombinedStackString == moveCombinedString) {
          return (true)
        }
       
        const found = findMoveInTreeRecursive(moves[i].to,moveCombinedString)

        // open up all parent branches
        if (found) {
          if (moves[i].canBranchAfterThisMove)
            moves[i].branchIsOpen = true
          return (true)
        }
      }
    return (found)
  }

  function buildMoveHistoryCombinedString() {
    var moveCombineds = [ initialFenidCombined ]
    for (var i = 0; i < moveHistoryStack.value.length; i++)
      moveCombineds.push(moveHistoryStack.value[i].toCombined)
    const moveCombinedString = moveCombineds.join(",")
    return (moveCombinedString)
  }

  // find boardmove in repertoire tree
  function findMoveInTree() {
    // get the move stack to find the move in the tree
    const moveCombinedString = buildMoveHistoryCombinedString()
    findMoveInTreeRecursive(initialFenidCombined,moveCombinedString)
  }

   // is boardmove  already in the visible tree?
   //    if yes, set selected and return true.
   function findMoveInTreeDisplay() {
    // get the move stack to find the move in the tree
    const moveCombinedString = buildMoveHistoryCombinedString()

    //console.log("find move in tree display",moveCombinedString)
    if (moveCombinedString == initialFenidCombined) {
      initialBoardSelectedInTree = true
      if (treeDisplay.value.length > 0)
        treeDisplay.value[0].moves[0].selected = true
      return (false)
    }

    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++) {
        if (treeDisplay.value[i].moves[j].move.fenidCombinedStackString == moveCombinedString) {
          //console.log("FOUND STRING IN TREE ",treeDisplay.value[i].moves[j])
          treeDisplay.value[i].moves[j].selected = true
          return (true)
        }
      }
    }
    return (false)
  }

  // build repertoire tree display. Allow for multiple open branches
  function buildRecursiveDisplayTreeView(parentFenidCombined,parentIsOpen,ply,indentLevel) {
    if (parentFenidCombined == initialFenidCombined) {
      treeDisplay.value = []
      treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel,moves : []})

      const oneMove = { 
                from : 0, 
                to : initialFenidCombined, 
                move_percentage : 0,
                san : t("Init"), 
                white : 0,
                count : 0,
                comment : "",
                fen : INITIAL_FEN,
                fenidCombinedStackString : "0~0," + initialFenidCombined
              }

      const oneTreeDisplayMove = {
              index : 0,
              ply : -1,
              indentLevel : 0,
              isOpen : false,
              selected : initialBoardSelectedInTree,
              parentIsOpen : false,
              move : oneMove
      }
      treeDisplay.value[0].moves.push(oneTreeDisplayMove)

    }

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      const toCombined = moves[i].to
  
      const oneTreeDisplayMove = {
              index : treeDisplay.value[treeDisplay.value.length-1].moves.length,
              ply : ply,
              indentLevel : indentLevel,
              isOpen : false,
              selected : false,
              parentIsOpen : parentIsOpen,
              move : moves[i]
      }
      if (parentIsOpen) {
        treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel+1,moves : []})
      }
      if (parentIsOpen || i == 0) {
        treeDisplay.value[treeDisplay.value.length-1].moves.push(oneTreeDisplayMove)
        buildRecursiveDisplayTreeView(moves[i].to,moves[i].branchIsOpen,ply+1,
                        (parentIsOpen) ? indentLevel+1: indentLevel)
      }
    }

    if (parentFenidCombined == initialFenidCombined) {
      treeTrigger.value++
    }
  }

  // walk entire repertoire building the list of combineds to get to this move
  function buildRepTreeFenidStack(parentFenidCombined,fenidCombinedStackString) {

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    const nextMoves = (moves.length != 1) ? null : repTree.get(moves[0].to)
    const canBranch = (nextMoves != null && nextMoves.length > 1)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      moves[i].canBranchAfterThisMove = canBranch
      moves[i].branchIsOpen = false
      moves[i].fenidCombinedStackString = fenidCombinedStackString + "," + moves[i].to
      buildRepTreeFenidStack(moves[i].to,moves[i].fenidCombinedStackString)
    }
  }

  function updateTreeView() {
    // clear the previous selection and see if the new move is already visible
    clearTreeDisplaySelectedFlag()

    const moveIsVisible = findMoveInTreeDisplay()

    // move isn't visible so find it in repertoire and open up parents
    if (moveIsVisible == false) {
      findMoveInTree()
      buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0 )
      findMoveInTreeDisplay()
    }
    treeTrigger.value++
  }


  // WORK - coming from reptree

  function toolbarUpdate(backDisabled,forwardDisabled) {
    //console.log("toolbar update  ",backDisabled,forwardDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,backDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FORWARD,forwardDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_TRIM,backDisabled)
  
    isLastMoveOnBranch = forwardDisabled

    toolbarTrigger.value++
  }

  function setupAlert(header,message,type,task,payload) {
    alertInfo.value = { header : header, message : message, type : type, task : task , payload : payload }
    alertTrigger.value++
  }

  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task,info)

    if (response == "no")
      return

    switch (task) {
      case "trim" :
        const move = info.payload
        if (backupHasBeenMade)
          deleteMoveFromRepertoire(move.fromCombined,move.toCombined)
        else {
          trimFromCombined = move.fromCombined
          trimToCombined = move.toCombined
          makeBackupThenDelete()
        }
        break
    }
  }

  function toolbarClick(index) {

    // trap that the button should be disabled but the screen hasn't caught up
    //var element = findToolbarElement(toolbarElements.value,index)
    //if (element != null && element.disabled)
    //  return
      
    switch (index) {
      case TOOLBAR_BACK :
        arrowPairs.value = []
        if (moveHistoryStack.value.length <= 1) {
          fen.value = INITIAL_FEN + " "
          initialBoardSelectedInTree = true

          moveHistoryStack.value = []
          moveHistoryTrigger.value++
          clearTreeDisplaySelectedFlag()
          const moveIsVisible = findMoveInTreeDisplay()
          toolbarUpdate(true,false)
        }
        else {

          moveHistoryStack.value.pop()
          moveHistoryTrigger.value++

          const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
          fen.value = oneMoveHistory.fen

          clearTreeDisplaySelectedFlag()
          const moveIsVisible = findMoveInTreeDisplay()
          toolbarUpdate(false,false)

        }
        updateTreeView()
        treeTrigger.value++
        break;
      case TOOLBAR_FORWARD :
        arrowPairs.value = []
        var lastMoveToCombined = initialFenidCombined
        if (moveHistoryStack.value.length > 0) {
          const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
          //console.log("Last Move on the stack",oneMoveHistory)
          lastMoveToCombined = oneMoveHistory.toCombined
        }
        if (repTree.has(lastMoveToCombined) == false)
          break
        const nextMoves = repTree.get(lastMoveToCombined)
        //console.log("NEXT MOVES ",nextMoves)
        const nextMove = nextMoves[0]
        
        fen.value = nextMove.fen
        const san = nextMove.san
        const boardToSquare = nextMove.toSquare
        const boardFromSquare = nextMove.fromSquare
        const percent = nextMove.move_percentage
        const oneMove = {fen : fen.value, 
                        fromCombined : nextMove.from,
                        toCombined : nextMove.to, 
                        boardToSquare : boardToSquare,
                        boardFromSquare : boardFromSquare,
                        san : san, movePercentage : percent,
                        comment : nextMove.comment }
        moveHistoryStack.value.push(oneMove)
        clearTreeDisplaySelectedFlag()
        const moveIsVisible = findMoveInTreeDisplay()

        // is there a move after this?
        const canForwardFromHere = repTree.has(nextMove.to)
        toolbarUpdate(false,!canForwardFromHere)
        break;
      case TOOLBAR_COMMENTS :
        showComments.value = !showComments.value
        setToolbarElementLabel(toolbarElements.value,TOOLBAR_COMMENTS,
                              (showComments.value) ? t("Hide Comments") : t("Show Comments"))
        toolbarTrigger.value++
        break
      case TOOLBAR_TRIM :
        const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
        var questionString = ""
        if (backupHasBeenMade == false)
          questionString = "Note: before the first 'tree trimming', a backup copy of the repertoire will be made. *** "
        if (isLastMoveOnBranch)
          questionString += "Remove " + oneMoveHistory.san + " ?"
        else
          questionString += "Remove " + oneMoveHistory.san + " AND all moves that follow it?"
        //console.log("question",questionString)
        setupAlert("Move Deletion?",questionString,"yesno","trim",oneMoveHistory)
        break
    }
  }

  function tourDone() {
    showTour.value = false
  }

  function processCoverageJsonResponse (json) {
    //console.log(" in coverage json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const next_program = json['next_program']

    const froms = json['froms'].split(",")
    const tos = json['tos'].split(",")
    const board_froms = json['board_froms'].split(",")
    const board_tos = json['board_tos'].split(",")
    const sans = json['sans'].split(",")
    const whites = json['whites'].split(",")
    const counts = json['counts'].split(",")
    const fens = json['fens'].split(",")
    const comments = json['comments'].split("\t")
    const move_percentages = json['move_percentage'].split(",")

    const count = Number(json['count'])

    repTree.clear();

    for (var i=0; i < count; i++) {
      const oneMove = { 
                from : froms[i], 
                to : tos[i], 
                fromSquare : board_froms[i],
                toSquare : board_tos[i],
                promotion : 0,
                move_percentage : Math.trunc(Number(move_percentages[i]) * 1000000),
                san : sans[i], 
                comment : (comments[i] == undefined) ? "" : comments[i],
                white : whites[i],
                count : counts[i],
                fen : fens[i],
                fenidCombinedStackString : ""
              }
      if (repTree.has(froms[i]) == false)
        repTree.set(froms[i],[])
      const toArray = repTree.get(froms[i])
      toArray.push(oneMove)
    }

    //console.log("repTree",repTree)
    buildRepTreeFenidStack(initialFenidCombined,initialFenidCombined)
    updateTreeView()
    toolbarUpdate(true,false)

    // if from trim, highlight the move before the trim. (I.e., do a "TOOLBAR_BACK")
    if (saveTrimMoveHistoryStack.length > 0) {
      moveHistoryStack.value = saveTrimMoveHistoryStack
      saveTrimMoveHistoryStack = []
      toolbarUpdate(false,false)
      toolbarClick(TOOLBAR_BACK)
      toolbarUpdate(true,true)
    }
    arrowPairs.value = []
  }

  function getCoverage() {

    if (repertoireId == "0")
      return

    var formData = {
      playing_white : playingWhite,
      repid : repertoireId,
      percentage : repertoirePercentage,
      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax - 1
    }

    //console.log("coverage postdata ",formData)
    useGetData("opening_coverage2.php", formData, processCoverageJsonResponse)
    
  }
  
  function processRepertoireInfoJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    repertoireId = json['repid']
    if (repertoireId != 0) {
      repertoireRatingMax = Number(json['rating_max']) + 1
      repertoireRatingMin = Number(json['rating_min'])
      repertoirePercentage = json['percentage']
      repertoireName = json['name']

      playingWhite = parseInt(json['playing_white'])
      if (playingWhite == 0)
        orientation.value = "black"

      repertoireDescription.value = t("Tree") + ": " + repertoireName + ", " + t("Rating") + ":" + repertoireRatingMin + "-" +
                repertoireRatingMax + " " + t("with") + " " + repertoirePercentage + "% " + t("Filter")
    }
    else
      repertoireDescription.value = "***** Use the Repertoire Manager to Select Repertoire ******"

    commentString.value = repertoireDescription.value

    getCoverage()
    menuTrigger.value++
  }

  function getRepertoireInfo() {
    var formData = { }
    useGetData("opening_repertoire_info.php", formData, processRepertoireInfoJsonResponse)
  }

  function processTrimTreeJsonResponse(json) {
    //console.log("*** in trim tree json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
    saveTrimMoveHistoryStack = moveHistoryStack.value
    moveHistoryStack.value = []
    getCoverage()
  }

  function deleteMoveFromRepertoire(fromCombined,toCombined) {
    if (repertoireId == "0")
      return

    var formData = {
      from : fromCombined,
      to : toCombined,
      repid : repertoireId
    }

    //console.log("before post for delete move from repertoire -> ",formData)

    useGetData("opening_trim_repertoire.php", formData, processTrimTreeJsonResponse)

  }

  function processBackupJsonResponse(json) {
    //console.log("repertoires backup json callback",json)
    if (json.hasOwnProperty('error')) {
      jsonError("ERROR while running -> " + json['programRun'] + "\n\n" + json['error'])
      return;
    }

    backupHasBeenMade = true

    deleteMoveFromRepertoire(trimFromCombined,trimToCombined)
  }

  function makeBackupThenDelete() {

    if (repertoireId == "0")
      return

    var formData = { 
      repid : repertoireId,
      new_name : "blah",
      backup : "yes"
    }
    //console.log("formdata in make backup repertoire",formData)
    useGetData("opening2_copy_repertoire.php", formData, processBackupJsonResponse)
  }

</script>

<style scoped>


</style>